import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import BusquedaPorIdentificador from "./BusquedaPorIdentificador";
import {
  exportarResumenEquiposDetalleExcel,
  getResumenEquipos,
  getResumenEquiposData,
  getResumenEquiposDetalle,
  getResumenModelos,
  getResumenServicios,
} from "../services/centroEquiposService";

const descargarBlob = (data, filename) => {
  const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ESTADOS = [
  ["tipo", "Tipo"],
  ["costo", "Costo"],
  ["asignadas", "Asignadas"],
  ["mantenimiento", "Mantenimiento"],
  ["disponible_inactivo", "Disponible Inactivo"],
  ["activo", "Activo"],
  ["traspaso", "Traspaso"],
  ["bajas", "Bajas"],
  ["robado", "Robado"],
  ["vendido", "Vendido"],
  ["vendido_activo", "Vendido Activo"],
  ["almacen", "Almacen"],
  ["programado", "Programado"],
  ["total", "Total"],
];

const ESTADOS_DETALLE = {
  asignadas: "Asignado",
  mantenimiento: "Mantenimiento",
  disponible_inactivo: "Inactivo",
  activo: "Activo",
  traspaso: "Transicion",
  bajas: "Baja",
  robado: "Robado",
  vendido: "Vendido",
  vendido_activo: "Vendido Activo",
  almacen: "Almacen",
  programado: "Programado",
  total: "Todos",
};

const RESUMEN_COLUMN_WIDTHS = {
  tipo: 230,
  costo: 70,
  asignadas: 82,
  mantenimiento: 112,
  disponible_inactivo: 136,
  activo: 64,
  traspaso: 76,
  bajas: 62,
  robado: 68,
  vendido: 76,
  vendido_activo: 114,
  almacen: 78,
  programado: 92,
  total: 62,
};

const CIUDADES = ["Nacional", "La Paz", "La Paz(El Alto)", "Santa Cruz", "Cochabamba", "Beni", "Pando", "Chuquisaca", "Potosi", "Tarija", "Oruro"];

const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
  "& .MuiSelect-select": { py: 0.35, px: 1 },
};

const fmt = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const unwrap = (payload, fallback) => payload?.data ?? payload ?? fallback;

const toDetalleTipo = (tipoEquipo) => {
  const value = String(tipoEquipo || "").toLowerCase();
  if (value === "equipo_modem") return "modem";
  if (value === "equipo_chip") return "chip";
  if (value === "equipo_bateria") return "bateria";
  if (value === "equipo_radio") return "radio";
  if (value === "equipo_varios") return "varios";
  return "";
};

const getDetalleId = (row) => row?.identificador || row?.num_sim || row?.numero || row?.serie || row?.id;

const renderAsignadoClientes = (item) => {
  const clientes = Array.isArray(item?.clientes) ? item.clientes : [];
  if (!clientes.length) return item?.asignado || "-";

  return (
    <Stack spacing={0.25}>
      {item?.asignado && (
        <Typography component="span" sx={{ fontSize: 11, fontWeight: 700 }}>
          {item.asignado}
        </Typography>
      )}
      {clientes.map((cliente, index) => (
        <Typography
          key={`${cliente.cod_servicio_cliente || index}-${cliente.cod_cliente || ""}`}
          component="span"
          title={[cliente.razon_social, cliente.descripcion].filter(Boolean).join(" - ")}
          sx={{ fontSize: 10.5, lineHeight: 1.25 }}
        >
          {[cliente.razon_social, cliente.descripcion].filter(Boolean).join(" - ") || cliente.cod_servicio_cliente}
        </Typography>
      ))}
    </Stack>
  );
};

export default function ResumenEquipos() {
  const [servicios, setServicios] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [servicio, setServicio] = useState("");
  const [equipo, setEquipo] = useState("");
  const [modelo, setModelo] = useState("");
  const [ciudad, setCiudad] = useState("Nacional");
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [error, setError] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [detalleParams, setDetalleParams] = useState(null);
  const [detallePage, setDetallePage] = useState(0);
  const [detallePageSize, setDetallePageSize] = useState(100);
  const [dialogEquipo, setDialogEquipo] = useState(null);
  const [exportando, setExportando] = useState(false);

  const handleExportarDetalle = async () => {
    if (!detalleParams) return;
    setExportando(true);
    setError("");
    try {
      const { data: blob } = await exportarResumenEquiposDetalleExcel(detalleParams);
      descargarBlob(blob, "resumen_equipos_detalle.xlsx");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo exportar a Excel");
    } finally {
      setExportando(false);
    }
  };

  const servicioSeleccionado = useMemo(
    () => servicios.find((item) => String(item.id_servicio) === String(servicio)),
    [servicio, servicios]
  );
  const equipoSeleccionado = useMemo(
    () => equipo === "__todos__"
      ? { id: "__todos__", nombre: "Todos" }
      : equipos.find((item) => String(item.id) === String(equipo)),
    [equipo, equipos]
  );
  const modeloSeleccionado = useMemo(
    () => modelos.find((item) => String(item.id_servicio_detalle) === String(modelo)),
    [modelo, modelos]
  );

  useEffect(() => {
    let alive = true;
    setCatalogLoading(true);
    getResumenServicios()
      .then(({ data }) => {
        if (alive) setServicios(Array.isArray(unwrap(data, [])) ? unwrap(data, []) : []);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.message || e?.message || "No se pudo cargar servicios");
      })
      .finally(() => {
        if (alive) setCatalogLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!servicioSeleccionado?.relacion) {
      setEquipos([]);
      setEquipo("");
      return;
    }

    let alive = true;
    setCatalogLoading(true);
    setError("");
    setEquipos([]);
    setEquipo("");
    setModelos([]);
    setModelo("");
    setResumen(null);

    getResumenEquipos(servicioSeleccionado.relacion)
      .then(({ data }) => {
        if (alive) setEquipos(Array.isArray(unwrap(data, [])) ? unwrap(data, []) : []);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.message || e?.message || "No se pudo cargar equipos");
      })
      .finally(() => {
        if (alive) setCatalogLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [servicioSeleccionado?.relacion]);

  useEffect(() => {
    if (equipo === "__todos__") {
      setModelos([]);
      setModelo("0");
      setResumen(null);
      return;
    }
    if (!equipoSeleccionado?.nombre) {
      setModelos([]);
      setModelo("");
      return;
    }

    let alive = true;
    setCatalogLoading(true);
    setError("");
    setModelos([]);
    setModelo("0");
    setResumen(null);

    getResumenModelos(equipoSeleccionado.nombre)
      .then(({ data }) => {
        if (alive) setModelos(Array.isArray(unwrap(data, [])) ? unwrap(data, []) : []);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.message || e?.message || "No se pudo cargar modelos");
      })
      .finally(() => {
        if (alive) setCatalogLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [equipo, equipoSeleccionado?.nombre]);

  const buscar = async () => {
    if (!servicioSeleccionado || !equipoSeleccionado || !modelo) return;
    try {
      setLoading(true);
      setError("");
      if (equipo === "__todos__") {
        const respuestas = await Promise.all(
          equipos.map(async (item) => {
            const { data } = await getResumenEquiposData({
              servicio: servicioSeleccionado.id_servicio,
              relacion: servicioSeleccionado.relacion,
              equipo: item.nombre,
              modelo: "0",
              ciudad,
            });
            return { equipo: item.nombre, resumen: unwrap(data, null) };
          })
        );
        const items = respuestas.flatMap(({ equipo: nombreEquipo, resumen: data }) =>
          (Array.isArray(data?.items) ? data.items : []).map((row) => ({
            ...row,
            equipo_nombre: nombreEquipo,
            modelo_tipo: row.tipo,
            tipo: `${nombreEquipo} - ${row.tipo}`,
          }))
        );
        setResumen({
          tipo: "Todos los equipos",
          total: respuestas.reduce((sum, item) => sum + Number(item.resumen?.total || 0), 0),
          items,
        });
      } else {
        const { data } = await getResumenEquiposData({
          servicio: servicioSeleccionado.id_servicio,
          relacion: servicioSeleccionado.relacion,
          equipo: equipoSeleccionado.nombre,
          modelo: modelo === "0" ? "0" : modeloSeleccionado?.id_servicio_detalle,
          ciudad,
        });
        setResumen(unwrap(data, null));
      }
      setDetalle(null);
      setSelectedCell(null);
      setDetalleParams(null);
      setDetallePage(0);
    } catch (e) {
      setResumen(null);
      setDetalle(null);
      setSelectedCell(null);
      setDetalleParams(null);
      setDetallePage(0);
      setError(e?.response?.data?.message || e?.message || "No se pudo generar el resumen");
    } finally {
      setLoading(false);
    }
  };

  const rows = resumen?.items || [];
  const total = Number(resumen?.total || 0);
  const esChipDetalle = detalle?.tipo === "equipo_chip";

  const cargarDetalle = async (params, page = detallePage, size = detallePageSize) => {
    try {
      setDetalleLoading(true);
      setError("");
      const { data } = await getResumenEquiposDetalle({
        ...params,
        page: page + 1,
        pageSize: size,
      });
      setDetalle(unwrap(data, null));
      setDetallePage(page);
    } catch (e) {
      setDetalle(null);
      setError(e?.response?.data?.message || e?.message || "No se pudo cargar el detalle");
    } finally {
      setDetalleLoading(false);
    }
  };

  const verDetalle = async (row, key) => {
    const value = Number(row?.[key] || 0);
    const nombreEquipo = row?.equipo_nombre || equipoSeleccionado?.nombre;
    const tipoModelo = row?.modelo_tipo || row?.tipo;
    if (!nombreEquipo || !tipoModelo || tipoModelo === "Totales" || key === "costo" || key === "tipo" || value <= 0) return;

    const cellId = `${nombreEquipo}-${tipoModelo}-${key}`;
    const params = {
      equipo: nombreEquipo,
      modelo: tipoModelo,
      estado: key,
      ciudad,
    };
    setSelectedCell(cellId);
    setDetalleParams(params);
    setDetallePage(0);
    await cargarDetalle(params, 0, detallePageSize);
  };

  const abrirDialogEquipo = (item) => {
    const detalleTipo = toDetalleTipo(item?.tipo_equipo);
    const detalleId = getDetalleId(item);

    if (!detalleTipo || !detalleId) {
      setError("Este tipo de equipo todavia no tiene detalle disponible.");
      return;
    }

    setDialogEquipo({
      tipo: detalleTipo,
      id: detalleId,
      titulo: `${item?.tipo_equipo || "Equipo"} ${detalleId}`,
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 0.75 }}>
      <Grid container spacing={0.75} alignItems="center">
        <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Servicio</InputLabel>
            <Select label="Servicio" value={servicio} onChange={(e) => setServicio(e.target.value)}>
              {servicios.map((item) => (
                <MenuItem key={item.id_servicio} value={item.id_servicio}>
                  {item.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
          <FormControl size="small" fullWidth sx={controlSx} disabled={!servicio || !equipos.length}>
            <InputLabel>Equipo</InputLabel>
            <Select label="Equipo" value={equipo} onChange={(e) => setEquipo(e.target.value)}>
              <MenuItem value="__todos__">Todos</MenuItem>
              {equipos.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl size="small" fullWidth sx={controlSx} disabled={!equipo || (equipo !== "__todos__" && !modelos.length)}>
            <InputLabel>Modelo</InputLabel>
            <Select label="Modelo" value={modelo} onChange={(e) => setModelo(e.target.value)}>
              <MenuItem value="0">Todos</MenuItem>
              {modelos.map((item) => (
                <MenuItem key={item.id_servicio_detalle} value={item.id_servicio_detalle}>
                  {item.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Ciudad</InputLabel>
            <Select label="Ciudad" value={ciudad} onChange={(e) => setCiudad(e.target.value)}>
              {CIUDADES.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 1.2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon fontSize="small" />}
            disabled={!servicio || !equipo || !modelo || loading}
            onClick={buscar}
            sx={{ minHeight: 30, height: 30, fontSize: 11.5, px: 1 }}
          >
            Buscar
          </Button>
        </Grid>
      </Grid>

      {catalogLoading && <LinearProgress sx={{ mt: 0.75 }} />}
      {error && <Alert severity="error" sx={{ mt: 0.75 }}>{error}</Alert>}

      <Stack direction="row" spacing={0.75} sx={{ my: 0.75 }} alignItems="center">
        <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
        {resumen?.tipo && <Chip size="small" variant="outlined" label={resumen.tipo} sx={{ height: 20, fontSize: 11 }} />}
      </Stack>

      {/* maxHeight acotado: TableContainer necesita su propio scroll interno
          para que stickyHeader funcione — sin una altura fija, MUI igual lo
          trata como contenedor de scroll (por el overflow-x:auto por
          defecto) pero nunca llega a desbordar, así que el header nunca se
          "pega" de verdad y termina desplazándose junto con el resto de la
          página. */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0.5, maxHeight: "65vh" }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            minWidth: 1320,
            "& .MuiTableCell-root": { py: 0.28, px: 0.55, fontSize: 11 },
          }}
        >
          <TableHead>
            <TableRow>
              {ESTADOS.map(([key, label]) => (
                <TableCell
                  key={key}
                  sx={{
                    fontWeight: 800,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.background.paper
                          : theme.palette.grey[100],
                    width: RESUMEN_COLUMN_WIDTHS[key],
                    whiteSpace: "nowrap",
                  }}
                  align={key === "tipo" ? "left" : "center"}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={ESTADOS.length}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Selecciona filtros y presiona Buscar.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row, index) => (
              <TableRow key={`${row.tipo}-${index}`} hover>
                {ESTADOS.map(([key]) => (
                  <TableCell
                    key={key}
                    align={key === "tipo" ? "left" : "center"}
                    sx={{
                      width: RESUMEN_COLUMN_WIDTHS[key],
                      whiteSpace: key === "tipo" ? "normal" : "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontVariantNumeric: key === "tipo" ? "normal" : "tabular-nums",
                    }}
                  >
                    {key !== "tipo" && key !== "costo" && row.tipo !== "Totales" && Number(row[key] || 0) > 0 ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => verDetalle(row, key)}
                        sx={{
                          border: 0,
                          background: selectedCell === `${row.equipo_nombre || equipoSeleccionado?.nombre}-${row.modelo_tipo || row.tipo}-${key}` ? "#dff3f5" : "transparent",
                          color: "#006b75",
                          cursor: "pointer",
                          font: "inherit",
                          fontVariantNumeric: "tabular-nums",
                          minWidth: 36,
                          px: 0.5,
                          py: 0.15,
                          borderRadius: 0.5,
                          textAlign: "center",
                          display: "inline-block",
                        }}
                      >
                        {fmt(row[key])}
                      </Box>
                    ) : (
                      fmt(row[key])
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {(detalleLoading || detalle) && (
        <Paper variant="outlined" sx={{ mt: 0.75, borderRadius: 0.5, overflow: "hidden" }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 0.75, py: 0.5, bgcolor: "#f7f9fa" }}>
            <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 800 }}>
              Equipos
            </Typography>
            {detalleLoading && <CircularProgress size={14} />}
            {detalle && (
              <>
                <Chip size="small" label={`${detalle.modelo || "-"}`} sx={{ height: 19, fontSize: 10.5 }} />
                <Chip size="small" label={ESTADOS_DETALLE[selectedCell?.split("-").pop()] || detalle.estado || "-"} sx={{ height: 19, fontSize: 10.5 }} />
                <Chip size="small" label={`Total: ${detalle.total || 0}`} sx={{ height: 19, fontSize: 10.5 }} />
              </>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              variant="outlined"
              startIcon={exportando ? <CircularProgress size={12} color="inherit" /> : <DownloadIcon fontSize="small" />}
              disabled={!detalle || exportando}
              onClick={handleExportarDetalle}
              sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
            >
              {exportando ? "Exportando..." : "Exportar Excel"}
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.25, px: 0.55, fontSize: 11 } }}>
              <TableHead>
                <TableRow>
                  {(esChipDetalle
                    ? ["Identificador", "Num", "Emei", "Modelo", "Modem", "Modelo Modem", "Plataforma", "Estado", "Ciudad", "Placa", "Propiedad", "Asignado / Cliente", "Fecha"]
                    : ["Identificador", "Modelo", "Estado", "Ciudad", "Placa", "Propiedad", "Asignado / Cliente", "Fecha"]
                  ).map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        fontWeight: 800,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.background.paper
                            : theme.palette.grey[100],
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!detalle?.items?.length ? (
                  <TableRow>
                    <TableCell colSpan={esChipDetalle ? 13 : 8}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        Sin equipos para mostrar.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : detalle.items.map((item, index) => (
                  <TableRow key={`${item.identificador}-${index}`} hover>
                    <TableCell>
                      {getDetalleId(item) ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => abrirDialogEquipo(item)}
                          style={{
                            border: 0,
                            background: "transparent",
                            color: "#006b75",
                            cursor: "pointer",
                            font: "inherit",
                            fontWeight: 700,
                            padding: 0,
                            textDecoration: "underline",
                          }}
                        >
                          {item.identificador || getDetalleId(item)}
                        </Box>
                      ) : "-"}
                    </TableCell>
                    {esChipDetalle && <TableCell>{item.num || "-"}</TableCell>}
                    {esChipDetalle && <TableCell>{item.emai || "-"}</TableCell>}
                    <TableCell>{item.modelo || "-"}</TableCell>
                    {esChipDetalle && <TableCell>{item.modem || "-"}</TableCell>}
                    {esChipDetalle && <TableCell>{item.modem_modelo || "-"}</TableCell>}
                    {esChipDetalle && <TableCell>{item.plataforma_reportando || "-"}</TableCell>}
                    <TableCell>{item.estado || "-"}</TableCell>
                    <TableCell>{item.ciudad || "-"}</TableCell>
                    <TableCell>{item.placa || "-"}</TableCell>
                    <TableCell>{item.propiedad || "-"}</TableCell>
                    <TableCell>{renderAsignadoClientes(item)}</TableCell>
                    <TableCell>{item.fecha_registro ? String(item.fecha_registro).slice(0, 10) : "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={detalle?.total || 0}
            page={detallePage}
            onPageChange={(_, page) => {
              if (detalleParams) cargarDetalle(detalleParams, page, detallePageSize);
            }}
            rowsPerPage={detallePageSize}
            onRowsPerPageChange={(e) => {
              const nextSize = Number(e.target.value);
              setDetallePageSize(nextSize);
              if (detalleParams) cargarDetalle(detalleParams, 0, nextSize);
            }}
            rowsPerPageOptions={[50, 100, 200, 500, 1000]}
            labelRowsPerPage="Limite"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              minHeight: 34,
              "& .MuiTablePagination-toolbar": { minHeight: 34, px: 1 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: 11,
                my: 0,
              },
              "& .MuiTablePagination-select": { fontSize: 11 },
            }}
          />
        </Paper>
      )}

      <Box sx={{ mt: 0.75 }}>
        <Typography variant="caption" color="text.secondary">
          Los modelos se cargan desde detalle_servicio usando el nombre del equipo seleccionado.
        </Typography>
      </Box>

      <Dialog
        open={Boolean(dialogEquipo)}
        onClose={() => setDialogEquipo(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ py: 0.75, px: 1.25, fontSize: 14, fontWeight: 800 }}>
          {dialogEquipo?.titulo || "Detalle de equipo"}
          <IconButton
            size="small"
            onClick={() => setDialogEquipo(null)}
            sx={{ position: "absolute", right: 8, top: 5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0.75 }}>
          {dialogEquipo && (
            <BusquedaPorIdentificador
              initialTipo={dialogEquipo.tipo}
              initialBuscar={dialogEquipo.id}
              autoLoadKey={`${dialogEquipo.tipo}-${dialogEquipo.id}`}
              hideSearch
            />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
